// Ce qui attend, toutes formations confondues.
//
// Tout se voyait par formation : on ouvrait une fiche, on lisait ses étapes,
// on repartait. Rien ne disait, le lundi matin, quelle satisfaction n'est
// jamais partie ni quelle formation finie reste à clôturer.
//
// Trois piles, parce qu'elles appellent trois gestes : ce qui n'est pas parti
// s'envoie, ce qui est parti sans réponse se relance, ce qui est fini se
// clôture. Les mélanger donnerait une liste qu'on parcourt sans savoir par où
// commencer.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Clock, CheckCircle2, ChevronRight } from 'lucide-react';
import api from '../api/client';

const PICTO = { a_envoyer: Send, sans_reponse: Clock, a_clore: CheckCircle2 };

// Le plus vieux d'une pile donne sa couleur : une satisfaction partie hier
// n'appelle pas le même regard qu'une qui traîne depuis cinq semaines.
const teinte = (jours) => (jours >= 30 ? 'text-[#B91C1C]'
  : jours >= 14 ? 'text-[#B45309]' : 'text-[#6B6C85]');

export default function FileQuestionnaires() {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const naviguer = useNavigate();

  useEffect(() => {
    api.get('/questionnaires/file').then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || !data.resume?.total) return null;
  const { piles, resume, libelles } = data;

  return (
    <div className="soft-card p-5" data-testid="file-questionnaires">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-[15.5px] font-semibold text-[#1A1B2E]"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Ce qui attend
          </h3>
          <p className="text-[12.5px] text-[#8E8FA5]">
            {resume.total} chose{resume.total > 1 ? 's' : ''} à faire sur vos formations
            {resume.doyen > 0 && ` — la plus ancienne depuis ${resume.doyen} jours`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.keys(libelles).map((nom) => {
          const lot = piles[nom] || [];
          const Icone = PICTO[nom] || Send;
          const deplie = ouvert === nom;
          const montres = deplie ? lot : lot.slice(0, 4);
          return (
            <div key={nom} data-testid={`file-pile-${nom}`}
                 className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Icone className="w-3.5 h-3.5 text-[#8E8FA5]" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6B6C85]">
                  {libelles[nom]}
                </span>
                <span className="ml-auto text-[13px] font-bold text-[#1A1B2E]">{lot.length}</span>
              </div>

              {lot.length === 0 ? (
                <div className="text-[11.5px] text-[#8E8FA5]">Rien n&rsquo;attend.</div>
              ) : (
                <ul className="space-y-1">
                  {montres.map((l, i) => (
                    <li key={`${l.session_id}-${l.code}-${i}`}>
                      <button
                        onClick={() => naviguer(`/sessions?focus=${l.session_id}`)}
                        className="w-full text-left bg-white rounded-lg border border-[#EFF0F6] px-2.5 py-1.5 hover:border-[#C7D2FE] group"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-[12px] font-semibold text-[#1A1B2E] truncate flex-1">
                            {l.formation}
                          </span>
                          <ChevronRight className="w-3 h-3 text-[#C7D2FE] opacity-0 group-hover:opacity-100" />
                        </div>
                        <div className="text-[11px] text-[#8E8FA5] truncate">
                          {l.etape} · {l.detail}
                        </div>
                        {typeof l.depuis === 'number' && l.depuis > 0 && (
                          <div className={`text-[10.5px] font-semibold ${teinte(l.depuis)}`}>
                            depuis {l.depuis} jour{l.depuis > 1 ? 's' : ''}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {lot.length > 4 && (
                <button
                  onClick={() => setOuvert(deplie ? null : nom)}
                  data-testid={`file-deplier-${nom}`}
                  className="mt-1.5 text-[11.5px] font-semibold text-[#4F46E5] hover:underline"
                >
                  {deplie ? 'Replier' : `Voir les ${lot.length}`}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
